import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  LogOut, 
  Heart,
  Home,
  ChevronRight,
  Search,
  Download,
  Eye,
  Check,
  X,
  Mail,
  Phone,
  Clock,
  MessageCircle,
  AlertCircle,
  User,
  FileText
} from 'lucide-react';

const AdminContacts = () => {
  const { user, logout, isAuthenticated, isReadOnly } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [loadingActions, setLoadingActions] = useState({});
  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchContacts();
  }, [isAuthenticated, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('contact_type', typeFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);

      const response = await fetch(`http://localhost:8000/api/public/contacts?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setContacts(data.data || []);
        // Pagination removed for simplicity
      } else {
        console.error('Erreur lors du chargement des contacts');
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateContactStatus = async (contactId, status) => {
    try {
      setLoadingActions(prev => ({ ...prev, [contactId]: status }));
      
      const response = await fetch(`http://localhost:8000/api/public/contacts/${contactId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        showNotification(`Contact marqué comme ${getStatusLabel(status)}`, 'success');
        fetchContacts();
      } else {
        showNotification('Erreur lors de la mise à jour', 'error');
      }
    } catch (error) {
      console.error('Update error:', error);
      showNotification('Erreur de connexion', 'error');
    } finally {
      setLoadingActions(prev => {
        const newState = { ...prev };
        delete newState[contactId];
        return newState;
      });
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contact ?')) {
      return;
    }

    try {
      const response = await fetch(`http://localhost:8000/api/public/contacts/${contactId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        showNotification('Contact supprimé avec succès', 'success');
        fetchContacts();
      } else {
        showNotification('Erreur lors de la suppression', 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showNotification('Erreur de connexion', 'error');
    }
  };

  const openContactModal = (contact) => {
    setSelectedContact(contact);
    setShowContactModal(true);
  };

  const closeContactModal = () => {
    setSelectedContact(null);
    setShowContactModal(false);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('contact_type', typeFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      
      const response = await fetch(`http://localhost:8000/api/public/contacts/export?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        
        // Créer un fichier CSV
        const headers = Object.keys(data.data[0] || {});
        const csvContent = [
          headers.join(','),
          ...data.data.map(row => headers.map(header => 
            `"${String(row[header] || '').replace(/"/g, '""')}"`
          ).join(','))
        ].join('\n');
        
        // Télécharger le fichier
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', data.filename.replace('.xlsx', '.csv'));
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        
        showNotification('Export réalisé avec succès', 'success');
      } else {
        showNotification('Erreur lors de l\'export', 'error');
      }
    } catch (error) {
      console.error('Export error:', error);
      showNotification('Erreur lors de l\'export', 'error');
    }
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusLabel = (status) => {
    const labels = {
      'nouveau': 'Nouveau',
      'en_cours': 'En cours',
      'resolu': 'Résolu',
      'ferme': 'Fermé'
    };
    return labels[status] || status;
  };

  const getStatusBadge = (status) => {
    const styles = {
      'nouveau': 'bg-blue-100 text-blue-800 border-blue-200',
      'en_cours': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'resolu': 'bg-green-100 text-green-800 border-green-200',
      'ferme': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${styles[status]}`}>
        {getStatusLabel(status)}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      'faible': 'bg-gray-100 text-gray-700',
      'normale': 'bg-blue-100 text-blue-700',
      'haute': 'bg-orange-100 text-orange-700',
      'urgente': 'bg-red-100 text-red-700'
    };

    const labels = {
      'faible': 'Faible',
      'normale': 'Normale', 
      'haute': 'Haute',
      'urgente': 'Urgente'
    };
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[priority]}`}>
        {labels[priority] || priority}
      </span>
    );
  };

  const getContactTypeLabel = (type) => {
    const labels = {
      'demande_information': 'Demande d\'information',
      'rejoindre_communaute': 'Rejoindre la communauté',
      'participation_evenement': 'Participation événement',
      'accompagnement_spirituel': 'Accompagnement spirituel',
      'partager_temoignage': 'Partage témoignage',
      'partenariat': 'Partenariat',
      'autre': 'Autre'
    };
    return labels[type] || type;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    fetchContacts();
  }, [searchTerm, statusFilter, typeFilter, priorityFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4 border-gradient-to-r from-blue-500 to-purple-600">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
            {/* Navigation breadcrumb */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="flex items-center space-x-1 sm:space-x-2 text-gray-600 text-sm sm:text-base">
                <Home className="w-3 h-3 sm:w-4 sm:h-4" />
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="font-medium text-gray-900 hidden sm:inline">Dashboard</span>
                <span className="font-medium text-gray-900 sm:hidden">Admin</span>
                <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-purple-600 font-semibold truncate">Gestion Contacts</span>
              </div>
            </div>

            {/* User info et actions */}
            <div className="flex items-center space-x-3 sm:space-x-6 w-full sm:w-auto justify-between sm:justify-end">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900">
                  {user?.name || 'Admin'}
                </p>
                <p className="text-xs text-gray-500 flex items-center justify-end">
                  <Heart className="w-3 h-3 mr-1 text-red-500" />
                  <span className="hidden lg:inline">Fleurs de la Sainte Famille</span>
                  <span className="lg:hidden">FSF</span>
                </p>
              </div>
              
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center px-2 sm:px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                title="Retour au Dashboard"
              >
                <Home className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Retour au Dashboard</span>
              </button>

              <button
                onClick={handleLogout}
                className="flex items-center px-2 sm:px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                title="Déconnexion"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Titre de la page */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            Gestion des Messages de Contact
          </h1>
          <p className="text-gray-600 text-sm sm:text-base lg:text-lg">
            Gérez les messages et demandes de contact de la communauté FSF
          </p>
        </div>

        {/* Filtres, recherche et export */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-3 sm:space-y-0">
            <h3 className="text-base sm:text-lg font-bold text-gray-900">
              Filtres et recherche
            </h3>
            <button
              onClick={handleExport}
              className="flex items-center px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 text-sm sm:text-base w-full sm:w-auto justify-center"
            >
              <Download className="w-4 h-4 mr-2" />
              <span className="sm:hidden">Export</span>
              <span className="hidden sm:inline">Exporter CSV</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Recherche */}
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Rechercher
              </label>
              <div className="relative">
                <Search className="w-4 h-4 sm:w-5 sm:h-5 absolute left-3 top-3 sm:top-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Nom, email ou sujet..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all text-sm sm:text-base"
                />
              </div>
            </div>

            {/* Filtre par statut */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Statut
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all text-sm sm:text-base"
              >
                <option value="all">Tous les statuts</option>
                <option value="nouveau">Nouveau</option>
                <option value="en_cours">En cours</option>
                <option value="resolu">Résolu</option>
                <option value="ferme">Fermé</option>
              </select>
            </div>

            {/* Filtre par type */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <span className="hidden sm:inline">Type de contact</span>
                <span className="sm:hidden">Type</span>
              </label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all text-sm sm:text-base"
              >
                <option value="all">Tous les types</option>
                <option value="demande_information">Demande d'information</option>
                <option value="rejoindre_communaute">Rejoindre la communauté</option>
                <option value="participation_evenement">Participation événement</option>
                <option value="accompagnement_spirituel">Accompagnement spirituel</option>
                <option value="partager_temoignage">Partage témoignage</option>
                <option value="partenariat">Partenariat</option>
                <option value="autre">Autre</option>
              </select>
            </div>

            {/* Filtre par priorité */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Priorité
              </label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all text-sm sm:text-base"
              >
                <option value="all">Toutes les priorités</option>
                <option value="faible">Faible</option>
                <option value="normale">Normale</option>
                <option value="haute">Haute</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
          </div>
        </div>

        {/* Liste des contacts */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-base sm:text-lg font-bold text-gray-900">
              Messages de contact ({contacts.length})
            </h3>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des messages...</p>
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-12">
              <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucun message trouvé</p>
            </div>
          ) : (
            <>
              {/* Affichage mobile en cartes */}
              <div className="block md:hidden">
                <div className="p-4 space-y-4">
                  {contacts.map((contact) => (
                    <div key={contact.id} className="bg-gray-50 rounded-lg p-4 space-y-3">
                      {/* En-tête de la carte */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 truncate">
                              {contact.first_name} {contact.last_name}
                            </p>
                            <p className="text-sm text-gray-500 truncate">{contact.email}</p>
                          </div>
                        </div>
                        <div className="flex space-x-1 flex-shrink-0">
                          <button
                            onClick={() => openContactModal(contact)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Lire le message"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Contenu de la carte */}AdminContacts={}
                      <div className="space-y-2">
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{contact.subject}</p>
                          <p className="text-sm text-gray-600 line-clamp-2">{contact.message}</p>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 text-xs">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                            {getContactTypeLabel(contact.contact_type)}
                          </span>
                          {getPriorityBadge(contact.priority)}
                          {getStatusBadge(contact.status)}
                        </div>
                        
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatDate(contact.created_at)}
                          </div>
                          
                          <div className="flex space-x-1">
                            {!isReadOnly && contact.status !== 'resolu' && (
                              <button
                                onClick={() => updateContactStatus(contact.id, 'resolu')}
                                disabled={loadingActions[contact.id]}
                                className={`p-1.5 rounded-lg transition-colors text-xs ${
                                  loadingActions[contact.id] === 'resolu'
                                    ? 'bg-green-100 text-green-400 cursor-not-allowed'
                                    : 'text-green-600 hover:bg-green-50'
                                }`}
                                title="Marquer comme résolu"
                              >
                                {loadingActions[contact.id] === 'resolu' ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600"></div>
                                ) : (
                                  <Check className="w-3 h-3" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Affichage desktop en tableau */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 lg:px-6 py-4 text-left text-sm font-semibold text-gray-700">Contact</th>
                      <th className="px-4 lg:px-6 py-4 text-left text-sm font-semibold text-gray-700 hidden lg:table-cell">Type</th>
                      <th className="px-4 lg:px-6 py-4 text-left text-sm font-semibold text-gray-700">Sujet</th>
                      <th className="px-4 lg:px-6 py-4 text-left text-sm font-semibold text-gray-700 hidden lg:table-cell">Priorité</th>
                      <th className="px-4 lg:px-6 py-4 text-left text-sm font-semibold text-gray-700">Statut</th>
                      <th className="px-4 lg:px-6 py-4 text-left text-sm font-semibold text-gray-700 hidden xl:table-cell">Date</th>
                      <th className="px-4 lg:px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {contacts.map((contact) => (
                      <tr key={contact.id} className="hover:bg-gray-50 transition-colors text-justify">
                        <td className="px-4 lg:px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <User className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm lg:text-base truncate">
                                {contact.first_name} {contact.last_name}
                              </p>
                              <div className="text-xs lg:text-sm text-gray-500 space-y-1">
                                <div className="flex items-center truncate">
                                  <Mail className="w-3 h-3 lg:w-4 lg:h-4 mr-1 flex-shrink-0" />
                                  <span className="truncate">{contact.email}</span>
                                </div>
                                {contact.phone && (
                                  <div className="flex items-center truncate lg:block">
                                    <Phone className="w-3 h-3 lg:w-4 lg:h-4 mr-1 flex-shrink-0 lg:hidden" />
                                    <span className="truncate lg:hidden">{contact.phone}</span>
                                    <div className="hidden lg:flex lg:items-center">
                                      <Phone className="w-4 h-4 mr-1" />
                                      {contact.phone}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 hidden lg:table-cell">
                          <span className="text-sm text-gray-700">
                            {getContactTypeLabel(contact.contact_type)}
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-4">
                          <p className="font-medium text-gray-900 text-sm lg:text-base truncate">{contact.subject}</p>
                          <p className="text-xs lg:text-sm text-gray-500 truncate max-w-xs lg:max-w-sm">
                            {contact.message}
                          </p>
                          {/* Affichage du type sur mobile */}
                          <div className="lg:hidden mt-1">
                            <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                              {getContactTypeLabel(contact.contact_type)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 hidden lg:table-cell">
                          {getPriorityBadge(contact.priority)}
                        </td>
                        <td className="px-4 lg:px-6 py-4">
                          <div className="space-y-1">
                            {getStatusBadge(contact.status)}
                            {/* Affichage de la priorité sur mobile */}
                            <div className="lg:hidden">
                              {getPriorityBadge(contact.priority)}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 hidden xl:table-cell">
                          <div className="flex items-center text-sm text-gray-600">
                            <Clock className="w-4 h-4 mr-2" />
                            {formatDate(contact.created_at)}
                          </div>
                        </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openContactModal(contact)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Lire le message"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                          {!isReadOnly && contact.status !== 'resolu' && (
                            <button
                              onClick={() => updateContactStatus(contact.id, 'resolu')}
                              disabled={loadingActions[contact.id]}
                              className={`p-2 rounded-lg transition-colors ${
                                loadingActions[contact.id] === 'resolu'
                                  ? 'bg-green-100 text-green-400 cursor-not-allowed'
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                              title="Marquer comme résolu"
                            >
                              {loadingActions[contact.id] === 'resolu' ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          {!isReadOnly && contact.status !== 'en_cours' && (
                            <button
                              onClick={() => updateContactStatus(contact.id, 'en_cours')}
                              disabled={loadingActions[contact.id]}
                              className={`p-1.5 lg:p-2 rounded-lg transition-colors ${
                                loadingActions[contact.id] === 'en_cours'
                                  ? 'bg-yellow-100 text-yellow-400 cursor-not-allowed'
                                  : 'text-yellow-600 hover:bg-yellow-50'
                              }`}
                              title="Marquer en cours"
                            >
                              {loadingActions[contact.id] === 'en_cours' ? (
                                <div className="animate-spin rounded-full h-3 w-3 lg:h-4 lg:w-4 border-b-2 border-yellow-600"></div>
                              ) : (
                                <Eye className="w-3 h-3 lg:w-4 lg:h-4" />
                              )}
                            </button>
                          )}
                          {!isReadOnly && (
                          <button
                            onClick={() => deleteContact(contact.id)}
                            className="p-1.5 lg:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Supprimer"
                          >
                            <X className="w-3 h-3 lg:w-4 lg:h-4" />
                          </button>
                          )}
                          {/* Date sur mobile/tablette */}
                          <div className="xl:hidden flex items-center text-xs text-gray-500 ml-2">
                            <Clock className="w-3 h-3 mr-1" />
                            <span className="hidden md:inline">{formatDate(contact.created_at)}</span>
                          </div>
                        </div>
                      </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          
          )}
        </div>

        {/* Modal de lecture du contact */}
        {showContactModal && selectedContact && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
              {/* Header du modal */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 sm:p-6 rounded-t-xl sm:rounded-t-2xl">
                <div className="flex justify-between items-start sm:items-center">
                  <div className="flex-1 pr-4">
                    <h2 className="text-lg sm:text-2xl font-bold flex items-center">
                      <FileText className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" />
                      <span className="truncate">Message de Contact</span>
                    </h2>
                    <p className="text-blue-100 mt-1 text-sm sm:text-base hidden sm:block">
                      Détails complets du message reçu
                    </p>
                  </div>
                  <button
                    onClick={closeContactModal}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1.5 sm:p-2 transition-colors flex-shrink-0"
                  >
                    <X className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                </div>
              </div>

              {/* Contenu du modal */}
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                {/* Informations personnelles */}
                <div className="bg-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 flex items-center">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600" />
                    Informations du Contact
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Prénom</label>
                      <p className="text-gray-900 font-medium">{selectedContact.first_name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Nom</label>
                      <p className="text-gray-900 font-medium">{selectedContact.last_name}</p>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Email</label>
                      <p className="text-gray-900 font-medium flex items-center break-all">
                        <Mail className="w-4 h-4 mr-2 text-gray-500 flex-shrink-0" />
                        <span className="break-all">{selectedContact.email}</span>
                      </p>
                    </div>
                    {selectedContact.phone && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">Téléphone</label>
                        <p className="text-gray-900 font-medium flex items-center">
                          <Phone className="w-4 h-4 mr-2 text-gray-500" />
                          {selectedContact.phone}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Détails du message */}
                <div className="bg-blue-50 rounded-lg sm:rounded-xl p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4 flex items-center">
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600" />
                    Détails du Message
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Type de contact</label>
                      <p className="text-gray-900 font-medium text-sm">{getContactTypeLabel(selectedContact.contact_type)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Priorité</label>
                      <div>{getPriorityBadge(selectedContact.priority)}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Statut</label>
                      <div>{getStatusBadge(selectedContact.status)}</div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 mb-1">Date de réception</label>
                      <p className="text-gray-900 font-medium flex items-center text-sm">
                        <Clock className="w-4 h-4 mr-2 text-gray-500" />
                        <span className="truncate">{formatDate(selectedContact.created_at)}</span>
                      </p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-600 mb-2">Sujet</label>
                    <p className="text-gray-900 font-medium text-base sm:text-lg break-words">{selectedContact.subject}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 mb-2">Message complet</label>
                    <div className="bg-white rounded-lg p-3 sm:p-4 border-2 border-gray-200 max-h-40 sm:max-h-60 overflow-y-auto">
                      <p className="text-gray-900 whitespace-pre-wrap leading-relaxed text-justify text-sm sm:text-base">{selectedContact.message}</p>
                    </div>
                  </div>
                </div>

                {/* Notes administratives */}
                {selectedContact.admin_notes && (
                  <div className="bg-yellow-50 rounded-lg sm:rounded-xl p-4 sm:p-6">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-yellow-600" />
                      Notes Administratives
                    </h3>
                    <div className="bg-white rounded-lg p-3 sm:p-4 border-2 border-yellow-200">
                      <p className="text-gray-900 whitespace-pre-wrap text-justify text-sm sm:text-base">{selectedContact.admin_notes}</p>
                    </div>
                  </div>
                )}

                {/* Actions rapides */}
                <div className="bg-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4">Actions Rapides</h3>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
                    {!isReadOnly && selectedContact.status !== 'resolu' && (
                      <button
                        onClick={() => {
                          updateContactStatus(selectedContact.id, 'resolu');
                          closeContactModal();
                        }}
                        className="flex items-center justify-center px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Marquer comme résolu
                      </button>
                    )}
                    {!isReadOnly && selectedContact.status !== 'en_cours' && (
                      <button
                        onClick={() => {
                          updateContactStatus(selectedContact.id, 'en_cours');
                          closeContactModal();
                        }}
                        className="flex items-center justify-center px-3 sm:px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm sm:text-base"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Marquer en cours
                      </button>
                    )}
                    <a
                      href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                      className="flex items-center justify-center px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Répondre par email
                    </a>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 rounded-b-xl sm:rounded-b-2xl border-t border-gray-200">
                <div className="flex justify-end">
                  <button
                    onClick={closeContactModal}
                    className="px-4 sm:px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notification */}
        {notification.show && (
          <div className={`fixed top-2 sm:top-4 right-2 sm:right-4 p-3 sm:p-4 rounded-lg shadow-lg z-50 flex items-center space-x-2 max-w-xs sm:max-w-sm ${
            notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {notification.type === 'success' ? <Check className="w-4 h-4 sm:w-5 sm:h-5" /> : <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" />}
            <span className="text-sm sm:text-base truncate">{notification.message}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContacts;